import { useEffect, useState } from 'react';
import { Boton } from '../../../componentes/Boton';
import { Chip } from '../../../componentes/Chip';
import { EstadoCargando, EstadoError } from '../../../componentes/EstadoPagina';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { Tabla } from '../../../componentes/Tabla';
import { estadoDeError } from '../../../logica/estados';
import { useCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { TarjetaAnillo } from '../Anillo';
import { useDatosServicios } from '../datos';
import { unir } from '../logica';
import { AYUDA, PERMISOS, SOLO_LECTURA } from '../textos';
import { FichaPlantilla } from './FichaPlantilla';
import { esPestanaPlantilla, serviciosQueLaPiden, textoValidez } from './logica';

type Filtro = 'activas' | 'archivadas';

/**
 * 9.11 «Consentimientos» (`?apartado=consentimientos`, ficha `&plantilla={id}&tab=`): las plantillas activas y archivadas
 * con qué servicios las piden y cuánto vale lo firmado, las firmas del mes en RV Anillo (de `getUsage`, si se puede leer)
 * y la ficha (`FichaPlantilla`). Sin el bloque Firma eIDAS, «No contratado» con «Añadir a mi plan» para el propietario, que
 * lleva a Plan con el bloque marcado mientras el cobro no está en la API (G120).
 */
export function Consentimientos(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, ruta, busqueda, navegar, membresia } = props;
    const { puede } = usePermisos();
    const gestiona = puede(PERMISOS.plantillas) && !soloLectura;
    const propietario = membresia?.is_owner === true;
    const { datos, error, reintentar, ponerPlantilla } = useDatosServicios(ops, negocio, puede);
    const [filtro, setFiltro] = useState<Filtro>('activas');
    const [firmas, setFirmas] = useState<{ usadas: number; incluidas: number } | null>(null);
    const abierta = busqueda.get('plantilla');
    const tab = busqueda.get('tab');
    const base = `${ruta}?apartado=consentimientos`;
    const abrir = (id: string | null, pestana?: string) => {
        navegar(id === null ? base : `${base}&plantilla=${encodeURIComponent(id)}${pestana === undefined ? '' : `&tab=${pestana}`}`, true);
    };
    const firma = datos?.firma === true;

    useCabecera({
        ayuda: AYUDA.consentimientos,
        acciones: gestiona && firma ? [{ clave: 'nueva', texto: 'Nueva plantilla', icono: 'plus' as const, onClick: () => { abrir('nueva'); } }] : [],
    });

    useEffect(() => {
        if (!firma) {
            return;
        }
        let vivo = true;
        ops.getUsage(negocio)
            .then((u) => {
                const c = u.data.find((x) => x.concept === 'signatures');
                if (vivo && c !== undefined) {
                    setFirmas({ usadas: c.used, incluidas: c.included + c.extra });
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, firma]);

    if (error !== null) {
        return <EstadoError seccion="tus plantillas de consentimiento" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={reintentar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="tus plantillas" />;
    }

    if (!datos.firma) {
        return (
            <div className="rv-sv-vacia">
                <span className="rv-sv-baldosa" style={{ background: 'var(--rv-ink)' }}>
                    <Icono nombre="signature" />
                </span>
                <Chip estado="neutral">No contratado</Chip>
                <strong>Pide que firmen los consentimientos antes de la cita</strong>
                <span>Con Firma eIDAS el cliente firma desde el móvil con validez legal, o en la tablet del centro si no lo hizo.</span>
                {propietario && !soloLectura ? (
                    <div className="rv-sv-vacia__botones">
                        <Boton icono="plus" movil={movil} onClick={() => { navegar('/plan?block=firma_eidas'); }}>
                            Añadir a mi plan
                        </Boton>
                    </div>
                ) : (
                    <span className="rv-sv-nota">
                        <Icono nombre="lock" />
                        <span>Pídeselo al propietario: solo él puede añadir bloques al plan.</span>
                    </span>
                )}
            </div>
        );
    }

    const lista = datos.plantillas.filter((t) => (filtro === 'activas') === (t.status === 'active'));
    const plantilla = abierta === null || abierta === 'nueva' ? null : (datos.plantillas.find((t) => t.id === abierta) ?? null);

    return (
        <>
            {soloLectura && (
                <p className="rv-sv-nota">
                    <Icono nombre="eye" />
                    <span>{SOLO_LECTURA}</span>
                </p>
            )}
            {firmas !== null && (
                <TarjetaAnillo valor={firmas.usadas} tope={firmas.incluidas} etiqueta="Firmas de este mes" detalle={`${String(firmas.usadas)} de ${String(firmas.incluidas)} incluidas`} nota="Las de más se suman a tu factura." />
            )}
            <Filtros
                etiqueta="Estado de las plantillas"
                items={[
                    { etiqueta: 'Activas', cifra: datos.plantillas.filter((t) => t.status === 'active').length },
                    { etiqueta: 'Archivadas', cifra: datos.plantillas.filter((t) => t.status === 'archived').length },
                ]}
                valor={filtro === 'activas' ? 0 : 1}
                onCambio={(i) => { setFiltro(i === 0 ? 'activas' : 'archivadas'); }}
            />
            <Tabla
                etiqueta="Plantillas de consentimiento"
                movil={movil}
                vacia={
                    <div className="rv-sv-vacia" style={{ boxShadow: 'none' }}>
                        <strong>{filtro === 'archivadas' ? 'No hay plantillas archivadas' : 'Todavía no tienes plantillas'}</strong>
                        <span>{filtro === 'archivadas' ? 'Las que archives dejan de poder pedirse, pero lo firmado se conserva.' : 'Escribe el consentimiento que firmarán tus clientes y elígelo en la ficha de cada servicio que lo pida.'}</span>
                    </div>
                }
                columnas={[
                    { clave: 'tpl', titulo: 'Plantilla', ancho: 'minmax(240px, 1.8fr)' },
                    { clave: 'piden', titulo: 'La piden', ancho: 'minmax(160px, 1.4fr)' },
                    { clave: 'vale', titulo: 'Lo firmado vale', ancho: '150px' },
                    { clave: 'estado', titulo: 'Estado', ancho: '120px' },
                ]}
                filas={lista.map((t) => {
                    const v = t.current_version;
                    const usos = serviciosQueLaPiden(t, datos.servicios);
                    const piden = usos.length === 0 ? 'Ningún servicio' : unir(usos.map((s) => s.current_version.name));
                    const estado = t.status === 'archived' ? 'Archivada' : usos.length === 0 ? 'Sin usar' : '';
                    const sub = `Versión ${String(v.number)} · firma ${v.signature_level === 'qualified' ? 'cualificada' : 'avanzada'}${v.identity_check ? ' · verifica la identidad' : ''}`;
                    return {
                        clave: t.id,
                        etiqueta: t.name,
                        onAbrir: () => { abrir(t.id); },
                        celdas: {
                            tpl: (
                                <span className="rv-sv-celda">
                                    <span className="rv-sv-baldosa" style={{ background: 'var(--rv-paper)', color: 'var(--rv-ink)' }}>
                                        <Icono nombre="file-text" />
                                    </span>
                                    <span className="rv-sv-celda__texto">
                                        <strong>{t.name}</strong>
                                        <small>{sub}</small>
                                    </span>
                                </span>
                            ),
                            piden,
                            vale: textoValidez(v.validity_days),
                            estado: estado === '' ? '' : <Chip estado="neutral">{estado}</Chip>,
                        },
                        tarjeta: (
                            <span className="rv-sv-celda">
                                <span className="rv-sv-baldosa" style={{ background: 'var(--rv-paper)', color: 'var(--rv-ink)' }}>
                                    <Icono nombre="file-text" />
                                </span>
                                <span className="rv-sv-celda__texto">
                                    <strong>{t.name}</strong>
                                    <small>{`${piden} · vale ${textoValidez(v.validity_days).toLocaleLowerCase('es')}`}</small>
                                    {estado !== '' && <Chip estado="neutral">{estado}</Chip>}
                                </span>
                            </span>
                        ),
                    };
                })}
            />
            {(abierta === 'nueva' && gestiona) || plantilla !== null ? (
                <FichaPlantilla
                    key={abierta ?? ''}
                    {...props}
                    datos={datos}
                    plantilla={plantilla}
                    pestana={esPestanaPlantilla(tab) ? tab : 'texto'}
                    gestiona={gestiona}
                    onPestana={(p) => { abrir(plantilla?.id ?? 'nueva', p); }}
                    onCerrar={() => { abrir(null); }}
                    onCreada={(t) => {
                        ponerPlantilla(t);
                        setFiltro('activas');
                        abrir(null);
                    }}
                    onCambiada={(t) => {
                        ponerPlantilla(t);
                        if (t.status === 'archived') {
                            abrir(null);
                        }
                    }}
                />
            ) : null}
        </>
    );
}
